import React, { useRef, useEffect, useCallback } from "react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselApi,
} from "./Carousel";
import {
    CombinedImage,
    MediaCarouselProps,
} from "../listings/listing/types";
import LeafletMap from "../listings/LeafletMap";
import HiddenAddressMapPlaceholder from "./HiddenAddressMapPlaceholder";
import Media from "./Media";
import { MdPermMedia } from "react-icons/md";
import { Star } from "lucide-react";
import { cn } from "../../utils/classMerger";

const MediaCarousel: React.FC<MediaCarouselProps> = ({
    setCarouselApi,
    combinedMedia,
    latitude,
    longitude,
    address,
    addressHidden = false,
    isMobile,
}) => {
    const carouselHeight = isMobile ? "h-64" : "h-80";

    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

    useEffect(() => {
        videoRefs.current = combinedMedia.map(
            (_, i) => videoRefs.current[i] || null
        );
    }, [combinedMedia]);

    const pauseAllVideos = useCallback(() => {
        videoRefs.current.forEach((video) => {
            if (video && !video.paused) {
                video.pause();
            }
        });
    }, []);

    const handleVideoPlay = useCallback((playingIndex: number) => {
        videoRefs.current.forEach((video, index) => {
            if (index !== playingIndex && video && !video.paused) {
                video.pause();
            }
        });
    }, []);

    const handleSelect = useCallback(() => {
        pauseAllVideos();
    }, [pauseAllVideos]);

    const onCarouselInit = useCallback(
        (api: CarouselApi) => {
            api?.on("select", handleSelect);
        },
        [handleSelect]
    );

    return (
        <div className="z-0 relative flex flex-col items-center justify-center">
            {combinedMedia.length > 0 ? (
                <div
                    className={`${
                        isMobile ? "mb-12" : "mb-8 w-full h-full"
                    } ${carouselHeight} z-0`}
                >
                    <Carousel
                        className={`w-full ${carouselHeight} relative`}
                        setApi={(api) => {
                            setCarouselApi?.(api);
                            onCarouselInit(api);
                        }}
                        isMobile={isMobile}
                    >
                        <CarouselContent>
                            {combinedMedia.map((media, index) => (
                                <CarouselItem key={index}>
                                    {media.type === "image" ? (
                                        // Shrink-wrap the image (h-full w-auto) so the inner div hugs
                                        // the actual photo — letting the Primary badge anchor to the
                                        // photo's real top-left corner instead of the letterbox gutter.
                                        <div className="flex h-full w-full items-center justify-center">
                                            <div className="relative h-full">
                                                {(media as CombinedImage)
                                                    .isPrimary && (
                                                    <span
                                                        className={cn(
                                                            "absolute z-10 inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-bold text-primary shadow-md",
                                                            isMobile
                                                                ? "left-4 top-4"
                                                                : "left-2 top-2"
                                                        )}
                                                    >
                                                        <Star className="h-3.5 w-3.5 fill-primary" />
                                                        Primary
                                                    </span>
                                                )}
                                                <img
                                                    src={
                                                        media.signedUrl ||
                                                        media.url
                                                    }
                                                    alt={`Media ${index}`}
                                                    loading="lazy"
                                                    className="h-full w-auto max-w-full object-contain"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <Media
                                            ref={
                                                media.type === "video"
                                                    ? (el: HTMLVideoElement) =>
                                                          (videoRefs.current[
                                                              index
                                                          ] = el)
                                                    : null
                                            }
                                            src={media.signedUrl || media.url}
                                            type={
                                                media.type as "image" | "video"
                                            }
                                            alt={`Media ${index}`}
                                            isMobile={isMobile}
                                            // On video play, pause all others
                                            onPlay={
                                                media.type === "video"
                                                    ? () =>
                                                          handleVideoPlay(index)
                                                    : undefined
                                            }
                                            thumbnail={media.thumbnail}
                                            isProcessing={
                                                media.type === "video"
                                                    ? media.isProcessing
                                                    : false
                                            }
                                        />
                                    )}
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                </div>
            ) : addressHidden ? (
                <HiddenAddressMapPlaceholder
                    isMobile={isMobile}
                    className={cn("my-4", !isMobile && "max-w-[1000px]")}
                />
            ) : latitude && longitude && address ? (
                <div className={`z-0 my-4 ${isMobile ? "h-60" : "h-80"}`}>
                    <LeafletMap
                        latitude={latitude}
                        longitude={longitude}
                        address={address}
                    />
                </div>
            ) : (
                <div
                    className={`flex flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50/50 p-6 text-center my-4 ${
                        isMobile ? "h-60 w-full" : "h-80 max-w-[1000px] w-full"
                    }`}
                >
                    <div className="flex flex-col items-center justify-center gap-2">
                        <MdPermMedia className="h-10 w-10 text-secondary" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaCarousel;
